// Görev Bağlama modalı — birden fazla görevi seçip sıraya dizerek gruplar.
// Hem yeni grup oluşturma hem mevcut grubu düzenleme için kullanılır.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Link2, X, ChevronUp, ChevronDown, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { tasksApi } from "../../lib/api";
import { GROUP_COLORS } from "../../lib/groupColors";
import { confirmDialog } from "../../lib/confirm";
import { useAuth } from "../../lib/auth";
import { kirliMi, KIRLI_UYARI } from "../../lib/formDirty";
import { useFormDraft } from "../../lib/useFormDraft";
import { DraftRestoreBar } from "../DraftRestoreBar";

export const LinkTasksModal = ({ candidateTasks = [], preselectedIds = [], group = null, onClose, onSaved, currentUser = null, taslakOtoYukle = false, onTaslakGeriYukle }) => {
  const isEdit = !!group;
  // Seçili görevler — SIRALI id listesi.
  const [selected, setSelected] = useState(() =>
    (preselectedIds || []).filter((id) => candidateTasks.some((t) => t.id === id))
  );
  const [name, setName] = useState(group?.name || "");
  const [color, setColor] = useState(group?.color || GROUP_COLORS[0].value);
  const [showProgress, setShowProgress] = useState(group ? !!group.show_progress : true);
  const [saving, setSaving] = useState(false);
  const { user: oturum } = useAuth();

  // FAZ F — kaydetmeden çıkışta uyarı.
  const anlikRef = useRef(null);
  anlikRef.current = { selected, name, color, showProgress };
  const baslangicRef = useRef(null);
  if (baslangicRef.current === null) baslangicRef.current = anlikRef.current;
  const tryClose = async () => {
    if (kirliMi(anlikRef.current, baslangicRef.current)) {
      const ok = await confirmDialog(KIRLI_UYARI);
      if (!ok) return;
      if (taslakYaz()) {
        if (onTaslakGeriYukle) {
          toast("Taslak kaydedildi", {
            action: { label: "GERİ YÜKLE", onClick: () => onTaslakGeriYukle(group?.id || null) },
            duration: 8000,
          });
        } else {
          toast("Taslak kaydedildi", { description: "Formu tekrar açtığınızda geri yükleyebilirsiniz", duration: 8000 });
        }
      }
    }
    onClose();
  };

  // FAZ G — taslak GRUP BAŞINA anahtarlanır (yeni grup için ayrı anahtar).
  const { teklif, setTeklif, yaz: taslakYaz, sil: taslakSilBu } = useFormDraft({
    userId: currentUser?.id ?? oturum?.id,
    formKey: group?.id ? `link-group:${group.id}` : "link-group-new",
    topla: () => ({ alanlar: anlikRef.current }),
    kirli: () => kirliMi(anlikRef.current, baslangicRef.current),
  });

  const geriYukle = (veri) => {
    const a = veri?.alanlar || {};
    // Silinmiş görevler taslaktan düşer.
    setSelected((a.selected || []).filter((id) => candidateTasks.some((t) => t.id === id)));
    setName(a.name ?? "");
    setColor(a.color || GROUP_COLORS[0].value);
    setShowProgress(a.showProgress !== false);
    setTeklif(null);
    toast.success("Taslak geri yüklendi");
  };

  useEffect(() => {
    if (taslakOtoYukle && teklif?.veri) geriYukle(teklif.veri);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taslakOtoYukle, teklif]);
  const taskById = useMemo(() => {
    const m = {};
    for (const t of candidateTasks) m[t.id] = t;
    return m;
  }, [candidateTasks]);

  const available = candidateTasks.filter((t) => !selected.includes(t.id));

  const addTask = (id) => setSelected((s) => [...s, id]);
  const removeTask = (id) => setSelected((s) => s.filter((x) => x !== id));
  const move = (idx, dir) => {
    setSelected((s) => {
      const next = [...s];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return s;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    if (selected.length < 2) {
      toast.error("Bağlamak için en az 2 görev seçin");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim() || null, color: color || null, show_progress: showProgress, task_ids: selected };
      if (isEdit) await tasksApi.updateGroup(group.id, payload);
      else await tasksApi.createGroup(payload);
      toast.success(isEdit ? "Grup güncellendi" : `${selected.length} görev bağlandı`);
      taslakSilBu();
      onSaved?.();
      onClose();
    } catch (e) {
      console.error("[LinkTasksModal] grup kaydetme hatası:", e);
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[110] bg-sertex-bg/70 backdrop-blur-sm" onClick={tryClose} data-testid="link-modal-backdrop" />
      <div className="fixed inset-0 z-[120] flex items-center justify-center pointer-events-none px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="pointer-events-auto w-full max-w-[520px] glass-panel corner-bracket p-4 space-y-3 max-h-[85vh] overflow-y-auto"
          data-testid="link-tasks-modal"
        >
          <div className="flex items-center justify-between border-b border-sertex-cyan/20 pb-2">
            <div className="display-text text-sertex-cyan neon-glow tracking-[0.2em] flex items-center gap-2">
              <Link2 className="h-4 w-4" /> {isEdit ? "GRUBU DÜZENLE" : "GÖREVLERİ BAĞLA"}
            </div>
            <button onClick={tryClose} data-testid="link-close" className="p-1 hover:bg-sertex-cyan/10 rounded text-sertex-textMuted hover:text-sertex-cyan">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Grup adı + ilerleme */}
          <div className="grid grid-cols-1 gap-2">
            <div>
              <div className="hud-text text-sertex-textMuted mb-1">GRUP ADI (opsiyonel)</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Örn: Cuma İşleri"
                data-testid="link-group-name"
                className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none"
              />
            </div>
            <label className="flex items-center gap-2 hud-text text-sertex-textMuted cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showProgress}
                onChange={(e) => setShowProgress(e.target.checked)}
                data-testid="link-show-progress"
                className="accent-sertex-cyan"
              />
              İlerleme göster (ör. "2/4 tamamlandı")
            </label>
            <div>
              <div className="hud-text text-sertex-textMuted mb-1">GRUP RENGİ</div>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="link-group-colors">
                {GROUP_COLORS.map((c) => {
                  const on = (color || "").toLowerCase() === c.value.toLowerCase();
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setColor(c.value)}
                      title={c.name}
                      data-testid={`link-color-${c.value.replace("#", "")}`}
                      className={`h-7 w-7 rounded-full transition-transform ${on ? "scale-110 ring-2 ring-offset-2 ring-offset-sertex-bg" : "hover:scale-105"}`}
                      style={{ background: c.value, boxShadow: on ? `0 0 10px ${c.value}` : "none", ...(on ? { "--tw-ring-color": c.value } : {}) }}
                    >
                      {on && <Check className="h-3.5 w-3.5 mx-auto text-black/70" />}
                    </button>
                  );
                })}
                <label className="h-7 w-7 rounded-full border border-dashed border-sertex-cyan/40 flex items-center justify-center cursor-pointer overflow-hidden relative" title="Özel renk">
                  <span className="h-4 w-4 rounded-full" style={{ background: color }} />
                  <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="link-color-custom" className="absolute inset-0 opacity-0 cursor-pointer" />
                </label>
              </div>
            </div>
          </div>

          {/* Seçili görevler — sıralı */}
          <div>
            <div className="hud-text text-sertex-cyan mb-1">SEÇİLİ GÖREVLER · SIRA ({selected.length})</div>
            {selected.length === 0 ? (
              <div className="hud-text text-sertex-textMuted/70 border border-dashed border-sertex-cyan/20 rounded-md px-3 py-3 text-center">
                Aşağıdan görev ekleyin
              </div>
            ) : (
              <div className="space-y-1" data-testid="link-selected-list">
                {selected.map((id, idx) => {
                  const t = taskById[id];
                  if (!t) return null;
                  return (
                    <div
                      key={id}
                      className="flex items-center gap-2 bg-sertex-surface/50 border border-sertex-cyan/25 rounded-md px-2 py-1.5"
                      data-testid={`link-selected-${id}`}
                    >
                      <span className="hud-text text-sertex-cyan w-5 text-center">{idx + 1}.</span>
                      <span className="flex-1 text-sm font-mono text-sertex-text truncate">{t.title}</span>
                      <button
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        title="Yukarı"
                        className="p-1 rounded text-sertex-textMuted hover:text-sertex-cyan disabled:opacity-30"
                        data-testid={`link-move-up-${id}`}
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => move(idx, 1)}
                        disabled={idx === selected.length - 1}
                        title="Aşağı"
                        className="p-1 rounded text-sertex-textMuted hover:text-sertex-cyan disabled:opacity-30"
                        data-testid={`link-move-down-${id}`}
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => removeTask(id)}
                        title="Çıkar"
                        className="p-1 rounded text-rose-300/80 hover:text-rose-300"
                        data-testid={`link-remove-${id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Eklenebilir görevler */}
          {available.length > 0 && (
            <div>
              <div className="hud-text text-sertex-textMuted mb-1">EKLENEBİLİR GÖREVLER</div>
              <div className="space-y-1 max-h-[200px] overflow-y-auto pr-1" data-testid="link-available-list">
                {available.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => addTask(t.id)}
                    data-testid={`link-add-${t.id}`}
                    className="w-full flex items-center gap-2 bg-sertex-surface/30 border border-sertex-cyan/15 hover:border-sertex-cyan/50 hover:bg-sertex-cyan/5 rounded-md px-2 py-1.5 text-left transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5 text-sertex-cyan shrink-0" />
                    <span className="flex-1 text-sm font-mono text-sertex-text truncate">{t.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-sertex-cyan/15">
            {teklif && !taslakOtoYukle && (
              <div className="w-full">
                <DraftRestoreBar
                  at={teklif.at}
                  onRestore={() => geriYukle(teklif.veri)}
                  onIgnore={() => { taslakSilBu(); toast("Taslak yoksayıldı"); }}
                  testid="link-draft-bar"
                />
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2 border-t border-sertex-cyan/15">
            <button
              onClick={tryClose}
              data-testid="link-cancel"
              className="flex-1 py-2 border border-sertex-cyan/25 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/50 rounded-md hud-text transition-colors"
            >
              İPTAL
            </button>
            <button
              onClick={handleSave}
              disabled={saving || selected.length < 2}
              data-testid="link-save"
              className="flex-1 py-2 bg-sertex-cyan/20 border border-sertex-cyan text-sertex-cyan hover:bg-sertex-cyan hover:text-sertex-bg rounded-md hud-text transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Check className="h-4 w-4" /> {isEdit ? "GÜNCELLE" : "BAĞLA"}
            </button>
          </div>
        </motion.div>
      </div>
    </>,
    document.body
  );
};

export default LinkTasksModal;
